import React, { useState, useEffect, useRef } from'react';
import axios from'axios';
import Api, { getApiUrl } from'../../services/api';
import MessageModal from'../common/MessageModal';
import { useAuth } from'../../context/AuthContext';

const Profile = ({ user, onLogout }) => {
 const { updateUser } = useAuth();
 const [stats, setStats] = useState({
 total: 0,
 completed: 0,
 pending: 0,
 totalSpent: 0
 });
 const [uploadingPicture, setUploadingPicture] = useState(false);
 const [profilePicturePreview, setProfilePicturePreview] = useState(null);
 const fileInputRef = useRef(null);
 const [editingField, setEditingField] = useState(null); //'username','email','phone','pincode','address', null
 const [editFormData, setEditFormData] = useState({ username:'', email:'', phone:'', pincode:'', address:''});
 const [updating, setUpdating] = useState(false);
 const [messageModal, setMessageModal] = useState({ show: false, message:'', type:'info', title:'Message'});
 const memberSinceLabel = user?.createdAt
 ? new Date(user.createdAt).toLocaleDateString('en-IN', {
 day:'numeric',
 month:'short',
 year:'numeric'
 })
 :'Recently';

 // Debug: Log messageModal changes
 useEffect(() => {
 console.log('📢 messageModal state changed:', messageModal);
 }, [messageModal]);

 // Auto-hide toast message after 2.5 seconds (non-blocking, no user interaction required)
 useEffect(() => {
 if (!messageModal.show) return undefined;
 console.log('⏰ Starting auto-hide timer for toast...');
 const timeoutId = setTimeout(() => {
 console.log('⏰ Auto-hiding toast after 2.5s');
 setMessageModal(prev => ({ ...prev, show: false }));
 }, 2500); // 2.5 seconds - perfect balance
 return () => {
 console.log('🧹 Cleaning up toast timer');
 clearTimeout(timeoutId);
 };
 }, [messageModal.show]);

 useEffect(() => {
 fetchUserRides();
 }, [user]);

 const fetchUserRides = async () => {
 try {
 const token = localStorage.getItem('uttaapp_token');
 const response = await axios.get(Api.Booking.myBookings, {
 headers: {'Authorization':`Bearer ${token}`}
 });

 if (response.data.success && response.data.bookings) {
 const bookings = response.data.bookings;
 
 // Calculate statistics
 const total = bookings.length;
 const completed = bookings.filter(b => b.status ==='completed'|| b.status ==='accepted').length;
 const pending = bookings.filter(b => b.status ==='pending'|| b.status ==='negotiating').length;
 const totalSpent = bookings
 .filter(b => b.fare && (b.status ==='completed'|| b.paymentStatus ==='completed'))
 .reduce((sum, b) => sum + (parseFloat(b.fare) || 0), 0);
 
 setStats({ total, completed, pending, totalSpent });
 }
 } catch (error) {
 console.error('Error fetching user rides:', error);
 }
 };

 const getProfileImageUrl = (profilePicture) => {
 if (!profilePicture) return null;
 // If it's a full URL, return as is
 if (profilePicture.startsWith('http')) return profilePicture;
 // Otherwise, construct backend URL using centralized API helper
 const baseUrl = getApiUrl();
 return`${baseUrl}/${profilePicture.replace(/\\/g,'/')}`;
 };

 const handleProfilePictureClick = () => {
 fileInputRef.current?.click();
 };

 const handleProfilePictureChange = async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;

 // Validate file type
 if (!file.type.startsWith('image/')) {
 setMessageModal({ show: true, message:'Please select an image file', type:'error', title:'Invalid File'});
 return;
 }

 // Show preview
 const reader = new FileReader();
 reader.onloadend = () => {
 setProfilePicturePreview(reader.result);
 };
 reader.readAsDataURL(file);

 // Upload profile picture
 setUploadingPicture(true);
 try {
 const formData = new FormData();
 formData.append('profilePicture', file);

 const token = localStorage.getItem('uttaapp_token');
 if (!token) {
 throw new Error('Authentication token not found. Please login again.');
 }

 console.log('📤 Uploading profile picture to:', Api.User.updateProfilePicture);
 console.log('📦 File details:', { name: file.name, type: file.type, size: file.size });
 
 const response = await axios.put(Api.User.updateProfilePicture, formData, {
 headers: {'Authorization':`Bearer ${token}`,'Content-Type':'multipart/form-data'}
 });

 console.log('📥 Profile picture upload response:', response.data);

 if (response.data.success) {
 // Get the updated profile picture from response
 const updatedProfilePicture = response.data.profilePicture || response.data.user?.profilePicture || response.data.data?.profilePicture;
 
 if (!updatedProfilePicture) {
 console.warn('⚠️ Profile picture updated but no profilePicture in response:', response.data);
 throw new Error('Profile picture updated but server did not return the new picture path');
 }

 // Update user context with new profile picture
 const updatedUser = { ...user, profilePicture: updatedProfilePicture };
 
 if (updateUser) {
 updateUser(updatedUser);
 console.log('✅ User context updated with new profile picture:', updatedProfilePicture);
 } else {
 console.warn('⚠️ updateUser function not available, updating localStorage directly');
 // Fallback: Update localStorage directly if updateUser is not available
 localStorage.setItem('uttaapp_user', JSON.stringify(updatedUser));
 }
 
 // Also ensure localStorage is updated (double-check)
 try {
 const storedUser = localStorage.getItem('uttaapp_user');
 if (storedUser) {
 const parsedUser = JSON.parse(storedUser);
 parsedUser.profilePicture = updatedProfilePicture;
 localStorage.setItem('uttaapp_user', JSON.stringify(parsedUser));
 console.log('✅ localStorage also updated with new profile picture');
 }
 } catch (localStorageError) {
 console.error('❌ Error updating localStorage:', localStorageError);
 }
 
 // Show non-blocking toast notification
 console.log('✅ Profile picture updated successfully in database!');
 const toastData = { 
 show: true, 
 message:'Profile picture updated successfully!', 
 type:'success', 
 title:''// No title for cleaner look
 };
 setMessageModal(toastData);
 setProfilePicturePreview(null);
 } else {
 throw new Error(response.data.message ||'Failed to update profile picture');
 }
 } catch (error) {
 console.error('Error updating profile picture:', error);
 setMessageModal({ show: true, message: error.response?.data?.message ||'Failed to update profile picture. Please try again.', type:'error', title:'Error'});
 setProfilePicturePreview(null);
 } finally {
 setUploadingPicture(false);
 // Reset file input
 if (fileInputRef.current) {
 fileInputRef.current.value ='';
 }
 }
 };

 // Handle edit field
 const handleEditClick = (field) => {
 setEditingField(field);
 setEditFormData({
 username: user?.username ||'',
 email: user?.email ||'',
 phone: user?.phone ||'',
 pincode: user?.pincode ||'',
 address: user?.address ||''});
 };

 // Handle cancel edit
 const handleCancelEdit = () => {
 setEditingField(null);
 setEditFormData({ username:'', email:'', phone:'', pincode:'', address:''});
 };

 // Handle save edit
 const handleSaveEdit = async () => {
 try {
 setUpdating(true);
 const token = localStorage.getItem('uttaapp_token');
 const updateData = {};
 
 if (editingField ==='username'&& editFormData.username) {
 updateData.username = editFormData.username;
 } else if (editingField ==='pincode'&& editFormData.pincode) {
 updateData.pincode = editFormData.pincode;
 } else if (editingField ==='address'&& editFormData.address) {
 updateData.address = editFormData.address;
 }

 if (Object.keys(updateData).length === 0) {
 setMessageModal({ show: true, message:'Please enter a value', type:'warning', title:'Validation Error'});
 setUpdating(false);
 return;
 }

 const response = await axios.put(Api.User.updateProfile, updateData, {
 headers: {'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}
 });

 if (response.data.success) {
 setMessageModal({ show: true, message:'Profile updated successfully!', type:'success', title:'Success'});
 setEditingField(null);
 // Update user context with merged data
 if (updateUser && response.data.user) {
 // Merge updated fields with existing user data to preserve all fields
 const updatedUser = {
 ...user, // Keep all existing user fields
 ...response.data.user // Override with updated fields from backend
 };
 updateUser(updatedUser);
 }
 // Don't reload - the state update will reflect changes immediately
 } else {
 throw new Error(response.data.message ||'Failed to update profile');
 }
 } catch (error) {
 console.error('Error updating profile:', error);
 setMessageModal({ show: true, message: error.response?.data?.message ||'Failed to update profile. Please try again.', type:'error', title:'Error'});
 } finally {
 setUpdating(false);
 }
 };

 return (
 <>
 <style>{`@keyframes slideDown {
 from {
 opacity: 0;
 transform: translate(-50%, -20px);
 }
 to {
 opacity: 1;
 transform: translate(-50%, 0);
 }
 }
 .animate-slideDown {
 animation: slideDown 0.3s ease-out forwards;
 }`}</style>
 <div className="bg-gray-50 pb-20 md:relative md:overflow-hidden md:bg-[linear-gradient(180deg,#edf7f2_0%,#f8fbf9_35%,#f4efe7_100%)] md:pb-0 md:"style={{ paddingBottom:'80px'}}>
 <div className="pointer-events-none absolute inset-0 hidden md:block">
 <div className="absolute left-[-8%] top-8 h-72 w-72 rounded-full bg-[#a6ddd0]/35 blur-3xl"/>
 <div className="absolute right-[-6%] top-20 h-80 w-80 rounded-full bg-[#efe3d2]/80 blur-3xl"/>
 <div className="absolute bottom-20 left-1/3 h-64 w-64 rounded-full bg-[#d8efe7]/50 blur-3xl"/>
 </div>
 <div className="md:hidden">
 <div className="relative overflow-hidden rounded-b-[30px] bg-[radial-gradient(circle_at_20%_10%,rgba(20,184,166,0.22),transparent_34%),linear-gradient(135deg,#063f3a_0%,#07584f_52%,#034239_100%)] px-6 pb-10 pt-5 text-white shadow-[0_26px_45px_-32px_rgba(3,64,57,0.85)]">
 <div className="pointer-events-none absolute -right-12 -top-10 h-44 w-44 rounded-full border border-white/10" />
 <div className="pointer-events-none absolute right-8 top-12 h-28 w-28 rounded-full border border-white/10" />
 <div className="relative flex items-center gap-4">
 <button
 type="button"
 onClick={handleProfilePictureClick}
 className="relative flex h-[88px] w-[88px] flex-shrink-0 items-center justify-center rounded-full bg-white/10 p-1 ring-2 ring-emerald-300/80"
 >
 <div className="relative h-full w-full overflow-hidden rounded-full bg-white/15">
 {profilePicturePreview ? (
 <img
 src={profilePicturePreview}
 alt="Profile Preview"
 className="h-full w-full object-cover"
 />
 ) : getProfileImageUrl(user?.profilePicture) ? (
 <img
 src={getProfileImageUrl(user.profilePicture)}
 alt="Profile"
 className="h-full w-full object-cover"
 onError={(e) => {
 e.target.style.display ='none';
 e.target.nextSibling.style.display ='flex';
 }}
 />
 ) : null}
 <div
 className="h-full w-full items-center justify-center bg-emerald-900/60"
 style={{ display: (profilePicturePreview || getProfileImageUrl(user?.profilePicture)) ?'none':'flex'}}
 >
 <span className="text-3xl font-black text-white">
 {user?.username ? user.username.charAt(0).toUpperCase() :'U'}
 </span>
 </div>
 </div>
 <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-4 border-[#06483f] bg-white text-[#0f766e] shadow-lg">
 <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h2l1.4-2.1A2 2 0 0110.06 4h3.88a2 2 0 011.66.9L17 7h2a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
 </svg>
 </span>
 {uploadingPicture && (
 <div className="absolute inset-1 flex items-center justify-center rounded-full bg-black/45">
 <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-white" />
 </div>
 )}
 </button>

 <div className="min-w-0 flex-1">
 <p className="text-[11px] font-semibold text-emerald-200">Hello,</p>
 <div className="mt-1 flex items-center gap-2">
 <h2 className="truncate text-[1.55rem] font-black leading-tight tracking-[-0.04em] text-white">
 {user?.username ||'User'}
 </h2>
 <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-400 text-[#06483f]">
 <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
 </svg>
 </span>
 </div>
 <p className="mt-1 truncate text-xs font-semibold text-white/78">
 {user?.email ||'user@example.com'}
 </p>
 <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-3 py-1.5 text-[11px] font-bold text-emerald-100 ring-1 ring-emerald-300/10">
 <span className="h-2 w-2 rounded-full bg-emerald-300" />
 Verified Account
 </div>
 </div>
 </div>
 </div>

 <div className="relative z-20 px-4 pt-3">
 <div className="rounded-[22px] bg-white p-3 shadow-[0_18px_38px_-28px_rgba(15,23,42,0.35)]">
 <div className="grid grid-cols-2 gap-3">
 <div className="rounded-[18px] border border-slate-100 bg-white px-4 py-4 shadow-[0_12px_24px_-24px_rgba(15,23,42,0.2)]">
 <div className="flex items-center gap-3">
 <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
 </svg>
 </div>
 <div>
 <p className="text-[10px] font-bold text-slate-400">Total Rides</p>
 <p className="mt-1 text-xl font-black text-[#0f766e]">{stats.total}</p>
 </div>
 </div>
 </div>
 <div className="rounded-[18px] border border-slate-100 bg-white px-4 py-4 shadow-[0_12px_24px_-24px_rgba(15,23,42,0.2)]">
 <div className="flex items-center gap-3">
 <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2m0-6h2a2 2 0 012 2v2a2 2 0 01-2 2h-2m0-6v6" />
 </svg>
 </div>
 <div>
 <p className="text-[10px] font-bold text-slate-400">Total Spent</p>
 <p className="mt-1 text-xl font-black text-[#0f766e]">₹{stats.totalSpent.toFixed(1)}</p>
 </div>
 </div>
 </div>
 </div>

 
 <div className="mt-3 grid grid-cols-2 divide-x divide-slate-100 rounded-[18px] bg-white">
 <button
 type="button"
 onClick={handleProfilePictureClick}
 className="flex items-center gap-3 px-3 py-3 text-left"
 >
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h2l1.4-2.1A2 2 0 0110.06 4h3.88a2 2 0 011.66.9L17 7h2a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
 </svg>
 </span>
 <span className="min-w-0 flex-1">
 <span className="block text-xs font-black text-slate-950">Change Photo</span>
 <span className="mt-0.5 block truncate text-[10px] text-slate-500">Update your profile picture</span>
 </span>
 <svg className="h-4 w-4 flex-shrink-0 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
 </svg>
 </button>
 <button
 type="button"
 onClick={() => handleEditClick('username')}
 className="flex items-center gap-3 px-3 py-3 text-left"
 >
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
 </svg>
 </span>
 <span className="min-w-0 flex-1">
 <span className="block text-xs font-black text-slate-950">Edit Name</span>
 <span className="mt-0.5 block truncate text-[10px] text-slate-500">Update your full name</span>
 </span>
 <svg className="h-4 w-4 flex-shrink-0 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
 </svg>
 </button>
 </div>
 </div>
 </div>
 </div>
 {/* Profile Header */}
 <div className="hidden bg-gradient-to-r from-primary-500 to-primary-600 py-8 md:block md:bg-transparent md:py-8">
 <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 md:max-w-7xl">
 <div className="text-center md:overflow-hidden md:rounded-[40px] md:border md:border-[#d6ebe4] md:bg-[linear-gradient(135deg,#0b5f58_0%,#117a72_45%,#f8f4ed_45%,#f3ece2_100%)] md:px-8 md:py-8 md:text-left md:shadow-[0_40px_90px_-60px_rgba(15,23,42,0.45)] md: md: md: lg:px-10 lg:py-10">
 <div className="md:grid md:items-center md:gap-8 lg:grid-cols-[0.88fr,1.12fr]">
 <div className="relative inline-block mb-4 md:mb-0 md:block">
 <div 
 onClick={handleProfilePictureClick}
 className="mx-auto flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-white transition-all group relative hover:ring-4 hover:ring-white/50 md:h-36 md:w-36 md:rounded-[34px] md:ring-8 md:ring-white/12 md:">
 {profilePicturePreview ? (
 <img 
 src={profilePicturePreview} 
 alt="Profile Preview"className="h-full w-full object-cover rounded-full md:rounded-[34px]"/>
 ) : getProfileImageUrl(user?.profilePicture) ? (
 <img 
 src={getProfileImageUrl(user.profilePicture)} 
 alt="Profile"className="h-full w-full object-cover rounded-full md:rounded-[34px]"onError={(e) => {
 e.target.style.display ='none';
 e.target.nextSibling.style.display ='flex';
 }}
 />
 ) : null}
 <div 
 className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center"style={{ display: (profilePicturePreview || getProfileImageUrl(user?.profilePicture)) ?'none':'flex'}}
 >
 <span className="text-2xl md:text-3xl font-bold text-white">
 {user?.username ? user.username.charAt(0).toUpperCase() :'U'}
 </span>
 </div>
 {/* Edit overlay */}
 <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 md:rounded-[34px]">
 <svg className="w-6 h-6 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
 </svg>
 </div>
 {uploadingPicture && (
 <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 md:rounded-[34px]">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
 </div>
 )}
 </div>
 <input
 ref={fileInputRef}
 type="file"accept="image/*"onChange={handleProfilePictureChange}
 className="hidden"/>
 </div>
 <div>
 <div className="hidden md:inline-flex md:items-center md:rounded-full md:border md:border-white/12 md:bg-white/12 md:px-4 md:py-2 md:text-[11px] md:font-semibold md:uppercase md:tracking-[0.24em] md:text-white/72 md: md: md:">
 Account overview
 </div>
 <h1 className="mb-2 text-2xl font-bold text-white md:mt-5 md:text-[4rem] md:font-extrabold md:leading-[0.92] md:tracking-[-0.06em] md:text-slate-950 md:">
 {user?.username ||'User'}
 </h1>
 <p className="text-lg text-primary-100 md:inline-flex md:rounded-full md:bg-white/82 md:px-5 md:py-3 md:text-base md:font-medium md:text-slate-700 md:shadow-[0_18px_45px_-30px_rgba(15,23,42,0.38)] md: md: md:">
 {user?.email ||'user@example.com'}
 </p>
 <p className="mt-2 text-sm text-primary-200 md:mt-4 md:max-w-xl md:text-sm md:text-slate-500 md:">
 Click on profile picture to update
 </p>
 <div className="mt-5 hidden md:grid md:grid-cols-3 md:gap-3">
 <div className="rounded-[24px] bg-white/88 px-4 py-4 shadow-[0_24px_55px_-38px_rgba(15,23,42,0.4)]">
 <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Total rides</div>
 <div className="mt-1 text-2xl font-bold text-slate-950">{stats.total}</div>
 </div>
 <div className="rounded-[24px] bg-white/88 px-4 py-4 shadow-[0_24px_55px_-38px_rgba(15,23,42,0.4)]">
 <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Completed</div>
 <div className="mt-1 text-2xl font-bold text-[#0f766e]">{stats.completed}</div>
 </div>
 <div className="rounded-[24px] bg-white/88 px-4 py-4 shadow-[0_24px_55px_-38px_rgba(15,23,42,0.4)]">
 <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Spent</div>
 <div className="mt-1 text-2xl font-bold text-slate-950">Rs.{stats.totalSpent.toFixed(2)}</div>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Profile Content */}
 <div className="relative z-10 mx-auto max-w-4xl px-4 py-5 sm:px-6 lg:px-8 md:max-w-7xl md:py-8">
 <div className="space-y-6">
 <div className="space-y-5 md:hidden">
 <div>
 <div className="mb-3 px-1">
 <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Overview</p>
 </div>
 <div className="rounded-[18px] bg-white p-4 shadow-[0_16px_34px_-28px_rgba(15,23,42,0.24)]">
 <div className="grid grid-cols-2 divide-x divide-slate-100">
 <div className="flex items-center gap-3 pr-3">
 <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
 </svg>
 </span>
 <div>
 <p className="text-[10px] font-bold text-slate-400">Account Status</p>
 <p className="mt-1 text-sm font-black text-slate-950">Verified</p>
 <p className="mt-0.5 text-[10px] text-slate-500">Ready for booking</p>
 <span className="mt-1.5 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">Active</span>
 </div>
 </div>
 <div className="flex items-center gap-3 pl-5">
 <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5v14M5 5h12l-1.5 4L17 13H5" />
 </svg>
 </span>
 <div>
 <p className="text-[10px] font-bold text-slate-400">Pending Trips</p>
 <p className="mt-1 text-lg font-black text-slate-950">{stats.pending}</p>
 <p className="mt-0.5 text-[10px] text-slate-500">Trips in progress</p>
 </div>
 </div>
 </div>
 </div>
 </div>

 <div>
 <div className="mb-3 px-1">
 <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Account Details</p>
 </div>
 <div className="overflow-hidden rounded-[18px] bg-white shadow-[0_16px_34px_-28px_rgba(15,23,42,0.24)]">
 <div className="divide-y divide-slate-100">
 <div className="px-4 py-4">
 {editingField ==='username'? (
 <div className="space-y-2">
 <input
 type="text"
 value={editFormData.username}
 onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
 className="w-full rounded-2xl border border-primary-500 bg-[#f7faf9] px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
 placeholder="Enter full name"
 />
 <div className="grid grid-cols-2 gap-2">
 <button
 type="button"
 onClick={handleSaveEdit}
 disabled={updating}
 className="rounded-2xl bg-[#0f766e] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
 >
 {updating ?'Saving...':'Save'}
 </button>
 <button
 type="button"
 onClick={handleCancelEdit}
 disabled={updating}
 className="rounded-2xl bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50"
 >
 Cancel
 </button>
 </div>
 </div>
 ) : (
 <button
 type="button"
 onClick={() => handleEditClick('username')}
 className="flex w-full items-center justify-between gap-3 text-left"
 >
 <span className="flex min-w-0 items-center gap-3">
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
 </svg>
 </span>
 <span className="min-w-0">
 <span className="block text-[10px] font-bold text-slate-400">Full Name</span>
 <span className="mt-1 block truncate text-sm font-black text-slate-950">{user?.username ||'Not provided'}</span>
 </span>
 </span>
 <svg className="h-4 w-4 flex-shrink-0 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
 </svg>
 </button>
 )}
 </div>

 <div className="flex items-center justify-between gap-3 px-4 py-4">
 <div className="flex min-w-0 items-center gap-3">
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
 </svg>
 </span>
 <span className="min-w-0">
 <span className="block text-[10px] font-bold text-slate-400">Email Address</span>
 <span className="mt-1 block truncate text-sm font-semibold text-slate-950">{user?.email ||'Not provided'}</span>
 </span>
 </div>
 <svg className="h-4 w-4 flex-shrink-0 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
 </svg>
 </div>

 <div className="flex items-center justify-between gap-3 px-4 py-4">
 <div className="flex min-w-0 items-center gap-3">
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
 </svg>
 </span>
 <span className="min-w-0">
 <span className="block text-[10px] font-bold text-slate-400">Phone Number</span>
 <span className="mt-1 block truncate text-sm font-semibold text-slate-950">{user?.phone ||'Not provided'}</span>
 </span>
 </div>
 <svg className="h-4 w-4 flex-shrink-0 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
 </svg>
 </div>

 <div className="flex items-center justify-between gap-3 px-4 py-4">
 <div className="flex min-w-0 items-center gap-3">
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#0f766e]">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
 </svg>
 </span>
 <span className="min-w-0">
 <span className="block text-[10px] font-bold text-slate-400">Member Since</span>
 <span className="mt-1 block truncate text-sm font-semibold text-slate-950">{memberSinceLabel}</span>
 </span>
 </div>
 <svg className="h-4 w-4 flex-shrink-0 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
 </svg>
 </div>
 </div>
 </div>
 </div>

 <button
 type="button"
 onClick={onLogout}
 className="flex w-full items-center justify-between rounded-[18px] bg-white px-4 py-4 text-left shadow-[0_16px_34px_-28px_rgba(15,23,42,0.24)]"
 >
 <span className="flex items-center gap-3">
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H9m4 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
 </svg>
 </span>
 <span className="text-sm font-black text-red-500">Logout</span>
 </span>
 <svg className="h-4 w-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
 </svg>
 </button>
 </div>

 {/* Personal Information */}
 <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-7 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md: md:">
 <h2 className="mb-4 flex items-center text-lg font-bold text-gray-900 md:mb-6 md:text-xl">
 <svg className="w-5 h-5 md:w-6 md:h-6 text-primary-600 mr-2 md:mr-3"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
 </svg>
 Personal Information
 </h2>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
 <div>
 <label className="mb-1.5 block text-xs font-medium text-gray-700 md:mb-2 md:text-sm">
 Username
 </label>
 {editingField ==='username'? (
 <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
 <input
 type="text"value={editFormData.username}
 onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
 className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"placeholder="Enter username"/>
 <div className="flex gap-2">
 <button
 onClick={handleSaveEdit}
 disabled={updating}
 className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap">
 {updating ?'Saving...':'Save'}
 </button>
 <button
 onClick={handleCancelEdit}
 disabled={updating}
 className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap">
 Cancel
 </button>
 </div>
 </div>
 ) : (
 <div className="flex items-center justify-between gap-2 rounded-lg border bg-gray-50 p-2.5 md:p-3">
 <span className="min-w-0 flex-1 truncate text-sm text-gray-900 md:text-base">{user?.username ||'Not provided'}</span>
 <button
 onClick={() => handleEditClick('username')}
 className="flex-shrink-0 p-1.5 text-gray-400 transition-colors hover:text-primary-600 md:p-1"title="Edit username">
 <svg className="w-4 h-4 md:w-4 md:h-4"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
 </svg>
 </button>
 </div>
 )}
 </div>
 
 <div>
 <label className="mb-1.5 block text-xs font-medium text-gray-700 md:mb-2 md:text-sm">
 Email Address
 </label>
 <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-2.5 md:p-3">
 <span className="min-w-0 flex-1 truncate text-sm text-gray-900 md:text-base">{user?.email ||'Not provided'}</span>
 </div>
 </div>
 
 <div>
 <label className="mb-1.5 block text-xs font-medium text-gray-700 md:mb-2 md:text-sm">
 Phone Number
 </label>
 <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-2.5 md:p-3">
 <span className="notranslate text-sm text-gray-900 md:text-base"translate="no">{user?.phone ||'Not provided'}</span>
 </div>
 </div>
 
 <div>
 <label className="mb-1.5 block text-xs font-medium text-gray-700 md:mb-2 md:text-sm">
 Pincode
 </label>
 {editingField ==='pincode'? (
 <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
 <input
 type="text"value={editFormData.pincode}
 onChange={(e) => setEditFormData({ ...editFormData, pincode: e.target.value })}
 className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"placeholder="Enter pincode"maxLength="6"/>
 <div className="flex gap-2">
 <button
 onClick={handleSaveEdit}
 disabled={updating}
 className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap">
 {updating ?'Saving...':'Save'}
 </button>
 <button
 onClick={handleCancelEdit}
 disabled={updating}
 className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap">
 Cancel
 </button>
 </div>
 </div>
 ) : (
 <div className="flex items-center justify-between gap-2 rounded-lg border bg-gray-50 p-2.5 md:p-3">
 <span className="min-w-0 flex-1 truncate text-sm text-gray-900 md:text-base">{user?.pincode ||'Not provided'}</span>
 <button
 onClick={() => handleEditClick('pincode')}
 className="flex-shrink-0 p-1.5 text-gray-400 transition-colors hover:text-primary-600 md:p-1"title="Edit pincode">
 <svg className="w-4 h-4 md:w-4 md:h-4"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
 </svg>
 </button>
 </div>
 )}
 </div>
 </div>
 
 <div className="mt-4 md:mt-6">
 <label className="mb-1.5 block text-xs font-medium text-gray-700 md:mb-2 md:text-sm">
 Address
 </label>
 {editingField ==='address'? (
 <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2">
 <textarea
 value={editFormData.address}
 onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
 className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"placeholder="Enter address"rows="3"/>
 <div className="flex sm:flex-col gap-2">
 <button
 onClick={handleSaveEdit}
 disabled={updating}
 className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap">
 {updating ?'Saving...':'Save'}
 </button>
 <button
 onClick={handleCancelEdit}
 disabled={updating}
 className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap">
 Cancel
 </button>
 </div>
 </div>
 ) : (
 <div className="flex items-start justify-between gap-2 rounded-lg border bg-gray-50 p-2.5 md:p-3">
 <span className="min-w-0 flex-1 break-words text-sm text-gray-900 md:text-base">{user?.address ||'Not provided'}</span>
 <button
 onClick={() => handleEditClick('address')}
 className="flex-shrink-0 p-1.5 text-gray-400 transition-colors hover:text-primary-600 md:p-1"title="Edit address">
 <svg className="w-4 h-4 md:w-4 md:h-4"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
 </svg>
 </button>
 </div>
 )}
 </div>
 </div>

 {/* Account Status */}
 <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-6 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md: md:">
 <h2 className="mb-6 flex items-center text-xl font-bold text-gray-900">
 <svg className="w-6 h-6 text-green-600 mr-3"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 Account Status
 </h2>
 
 <div className="flex flex-col gap-3 rounded-[22px] border border-green-200 bg-green-50 p-4 md:flex-row md:items-center md:justify-between md:rounded-[24px] md:bg-[linear-gradient(135deg,#ecfdf5_0%,#f4fbf7_100%)] md:px-5 md:py-5 md:">
 <div className="flex items-center">
 <svg className="w-6 h-6 text-green-600 mr-3"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M5 13l4 4L19 7"/>
 </svg>
 <div>
 <p className="font-medium text-green-800">Account Verified</p>
 <p className="text-sm text-green-600">Your account is active and verified</p>
 </div>
 </div>
 <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
 Active
 </span>
 </div>
 </div>

 {/* Ride Statistics */}
 <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-6 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md: md:">
 <h2 className="mb-6 flex items-center text-xl font-bold text-gray-900">
 <svg className="w-6 h-6 text-blue-600 mr-3"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
 </svg>
 Ride Statistics
 </h2>
 
 <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-3">
 <div className="rounded-lg border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-4 text-center md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md: md:">
 <div className="text-2xl font-bold text-blue-600 md:text-[2rem] md:text-slate-950 md:">{stats.total}</div>
 <div className="text-sm font-medium text-blue-800 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:">Total Rides</div>
 </div>
 <div className="rounded-lg border-2 border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-4 text-center md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md: md:">
 <div className="text-2xl font-bold text-green-600 md:text-[2rem] md:text-[#0f766e]">{stats.completed}</div>
 <div className="text-sm font-medium text-green-800 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:">Completed</div>
 </div>
 <div className="rounded-lg border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 text-center md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md: md:">
 <div className="text-2xl font-bold text-yellow-600 md:text-[2rem] md:text-[#9a6700]">{stats.pending}</div>
 <div className="text-sm font-medium text-yellow-800 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:">Pending</div>
 </div>
 <div className="rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 p-4 text-center md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md: md:">
 <div className="text-2xl font-bold text-purple-600 md:text-[2rem] md:text-slate-100">Rs.{stats.totalSpent.toFixed(2)}</div>
 <div className="text-sm font-medium text-purple-800 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:">Total Spent</div>
 </div>
 </div>
 </div>


 {/* Quick Actions */}
 <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-6 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md: md:">
 <h2 className="mb-6 flex items-center text-xl font-bold text-gray-900">
 <svg className="w-6 h-6 text-primary-600 mr-3"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
 </svg>
 Quick Actions
 </h2>
 
 <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
 <button className="flex items-center rounded-[22px] border border-primary-200 bg-primary-50 p-4 transition-colors hover:bg-primary-100 md:rounded-[24px] md:bg-[linear-gradient(135deg,#ecfdf5_0%,#f0faf7_100%)] md:">
 <svg className="w-6 h-6 text-primary-600 mr-3"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
 </svg>
 <div className="text-left">
 <p className="font-medium text-primary-800">Edit Profile</p>
 <p className="text-sm text-primary-600">Update your information</p>
 </div>
 </button>
 
 <button className="flex items-center rounded-[22px] border border-green-200 bg-green-50 p-4 transition-colors hover:bg-green-100 md:rounded-[24px] md:bg-[linear-gradient(135deg,#effcf4_0%,#f4fbf7_100%)] md:">
 <svg className="w-6 h-6 text-green-600 mr-3"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
 </svg>
 <div className="text-left">
 <p className="font-medium text-green-800">Ride History</p>
 <p className="text-sm text-green-600">View past rides</p>
 </div>
 </button>
 </div>
 </div>
 </div>
 </div>

 {/* Non-blocking Toast Notification - Appears automatically, disappears after 2.5s */}
 {messageModal.show && (
 <div 
 className="fixed w-[90%] max-w-md animate-slideDown"style={{ 
 zIndex: 999999,
 position:'fixed',
 top:'80px',
 left:'50%',
 transform:'translateX(-50%)',
 pointerEvents:'auto'}}
 role="alert"aria-live="polite">
 <div
 className={`px-5 py-4 rounded-xl shadow-2xl flex items-center gap-3 text-white border-2 ${
 messageModal.type ==='success'?'bg-green-600 border-green-400': messageModal.type ==='error'?'bg-red-600 border-red-400': messageModal.type ==='warning'?'bg-orange-600 border-orange-400':'bg-blue-600 border-blue-400'}`}
 style={{ boxShadow:'0 10px 40px rgba(0,0,0,0.3)'}}
 >
 {/* Success Icon */}
 {messageModal.type ==='success'&& (
 <svg className="w-6 h-6 flex-shrink-0"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2.5} d="M5 13l4 4L19 7"/>
 </svg>
 )}
 {/* Error Icon */}
 {messageModal.type ==='error'&& (
 <svg className="w-6 h-6 flex-shrink-0"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2.5} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 )}
 {/* Message Text */}
 <div className="flex-1">
 {messageModal.title && (
 <div className="text-base font-bold mb-1">{messageModal.title}</div>
 )}
 <div className="text-base font-semibold">{messageModal.message}</div>
 </div>
 {/* Optional Close Button (doesn't block, just allows manual dismiss) */}
 <button
 type="button"onClick={() => setMessageModal({ show: false, message:'', type:'info', title:''})}
 className="text-white/80 hover:text-white flex-shrink-0 ml-1 transition-opacity"aria-label="Close notification">
 <svg className="w-4 h-4"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>
 </div>
 )}

 {/* Message Modal - Only show for errors/warnings, not for success */}
 {messageModal.show && messageModal.type !=='success'&& (
 <MessageModal
 message={messageModal.message}
 title={messageModal.title}
 type={messageModal.type}
 onClose={() => setMessageModal({ show: false, message:'', type:'info', title:'Message'})}
 />
 )}
 </div>
 </>
 );
};

export default Profile;
